import useLayoutStore from '../Store/layoutStore';

const widgetLayoutStates = new WeakMap();

function getWidgetLayoutState(widget) {
  let state = widgetLayoutStates.get(widget);
  if (!state) {
    state = { controller: null, managerModal: null, pendingLoad: null };
    widgetLayoutStates.set(widget, state);
  }
  return state;
}

export function registerWidgetLayoutLoadController(widget, controller) {
  const state = getWidgetLayoutState(widget);
  state.controller = controller;
  return () => {
    if (state.controller === controller) state.controller = null;
  };
}

function errorText(error, fallback) {
  return error instanceof Error && error.message ? error.message : fallback;
}

function styledButton(label) {
  const button = document.createElement('button');
  button.type = 'button';
  button.textContent = label;
  Object.assign(button.style, {
    backgroundColor: '#000',
    color: '#B18800',
    border: '1px solid #B18800',
    padding: '6px 10px',
    borderRadius: '4px',
    cursor: 'pointer',
  });
  return button;
}

export function getLoadableLayoutContent(record) {
  let content = record?.content;
  if (typeof content === 'string') {
    try {
      content = JSON.parse(content);
    } catch {
      throw new Error('The saved layout contains invalid chart state.');
    }
  }
  if (!content || typeof content !== 'object' || Array.isArray(content)) {
    throw new Error('The saved layout does not contain chart state.');
  }
  return content;
}

export function requestWidgetLayoutLoad(widget, content) {
  const state = getWidgetLayoutState(widget);
  if (state.pendingLoad) {
    return Promise.reject(new Error('A saved layout is already loading.'));
  }
  if (typeof state.controller !== 'function') {
    return Promise.reject(new Error('The current chart is not ready to load a layout.'));
  }

  const pendingLoad = Promise.resolve().then(() => state.controller(content));
  state.pendingLoad = pendingLoad;
  return pendingLoad.finally(() => {
    if (state.pendingLoad === pendingLoad) state.pendingLoad = null;
  });
}

export function showLayoutManagerPopup(tvWidgetRef, memberId) {
  const widget = tvWidgetRef?.current;
  const widgetState = widget ? getWidgetLayoutState(widget) : null;
  if (widgetState?.managerModal && document.body.contains(widgetState.managerModal)) {
    widgetState.managerModal.focus?.();
    return widgetState.managerModal;
  }

  const modal = document.createElement('div');
  modal.dataset.chartLayoutModal = 'manager';
  Object.assign(modal.style, {
    position: 'fixed',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#000',
    color: '#B18800',
    border: '1px solid #B18800',
    padding: '20px',
    zIndex: '10000',
    borderRadius: '8px',
    minWidth: '300px',
    boxShadow: '0 0 10px #B18800',
  });

  const title = document.createElement('h3');
  title.textContent = 'Select a Layout';
  title.style.marginBottom = '12px';

  const dropdown = document.createElement('select');
  dropdown.setAttribute('aria-label', 'Saved layouts');
  Object.assign(dropdown.style, {
    backgroundColor: '#000',
    color: '#B18800',
    border: '1px solid #B18800',
    padding: '6px',
    width: '100%',
    marginBottom: '12px',
  });

  const error = document.createElement('p');
  error.setAttribute('role', 'alert');
  error.style.minHeight = '1.2em';
  error.style.margin = '0 0 12px';

  const buttonRow = document.createElement('div');
  buttonRow.style.display = 'flex';
  buttonRow.style.justifyContent = 'space-between';
  const loadButton = styledButton('Load');
  const deleteButton = styledButton('Delete');
  const closeButton = styledButton('Close');
  let loadPending = false;
  let deleteConfirmModal = null;
  const closeDeleteConfirm = () => {
    if (deleteConfirmModal && document.body.contains(deleteConfirmModal)) {
      document.body.removeChild(deleteConfirmModal);
    }
    deleteConfirmModal = null;
  };
  const closeModal = () => {
    closeDeleteConfirm();
    if (document.body.contains(modal)) document.body.removeChild(modal);
    if (widgetState?.managerModal === modal) widgetState.managerModal = null;
  };

  const renderLayouts = (layouts) => {
    dropdown.replaceChildren();
    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = layouts.length ? 'Select a layout...' : 'No saved layouts';
    placeholder.disabled = true;
    placeholder.selected = true;
    dropdown.appendChild(placeholder);
    layouts.forEach((layout) => {
      const option = document.createElement('option');
      option.value = String(layout.id);
      option.textContent = layout.name;
      dropdown.appendChild(option);
    });
  };

  const loadLayouts = async () => {
    error.textContent = '';
    try {
      const layouts = await useLayoutStore.getState().loadLayouts(memberId);
      renderLayouts(layouts);
      return true;
    } catch (loadError) {
      renderLayouts([]);
      error.textContent = errorText(loadError, 'Failed to load saved layouts.');
      return false;
    }
  };

  loadButton.onclick = async () => {
    if (loadPending) return;
    error.textContent = '';
    const layoutId = dropdown.value;
    if (!layoutId) {
      error.textContent = 'Select a layout.';
      return;
    }

    loadPending = true;
    loadButton.disabled = true;
    try {
      if (!widget) {
        throw new Error('The current chart is not ready to load a layout.');
      }
      const record = await useLayoutStore.getState().loadLayout({ layoutId, memberId });
      const content = getLoadableLayoutContent(record);
      await requestWidgetLayoutLoad(widget, content);
      closeModal();
    } catch (loadError) {
      error.textContent = errorText(loadError, 'Failed to load the saved layout.');
      loadPending = false;
      loadButton.disabled = false;
    }
  };

  deleteButton.onclick = () => {
    error.textContent = '';
    const layoutId = dropdown.value;
    if (!layoutId) {
      error.textContent = 'Select a layout.';
      return;
    }
    const existingConfirmModal = document.querySelector('[data-chart-layout-modal="delete"]');
    if (existingConfirmModal) {
      existingConfirmModal.focus?.();
      return;
    }
    const layoutName = dropdown.selectedOptions[0]?.textContent?.trim() || 'selected layout';

    const confirmModal = document.createElement('div');
    deleteConfirmModal = confirmModal;
    confirmModal.dataset.chartLayoutModal = 'delete';
    Object.assign(confirmModal.style, {
      position: 'fixed',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      backgroundColor: '#000',
      color: '#B18800',
      border: '1px solid #B18800',
      padding: '20px',
      zIndex: '10001',
      borderRadius: '8px',
      boxShadow: '0 0 10px #B18800',
    });
    const message = document.createElement('p');
    message.textContent = `Delete "${layoutName}"?`;
    const confirmError = document.createElement('p');
    confirmError.setAttribute('role', 'alert');
    confirmError.style.minHeight = '1.2em';
    const yesButton = styledButton('Yes');
    const noButton = styledButton('No');
    yesButton.style.marginRight = '10px';

    yesButton.onclick = async () => {
      confirmError.textContent = '';
      yesButton.disabled = true;
      try {
        await useLayoutStore.getState().deleteLayout({ layoutId, memberId });
      } catch (deleteError) {
        confirmError.textContent = errorText(deleteError, 'Failed to delete the layout.');
        yesButton.disabled = false;
        return;
      }

      const selectedOption = Array.from(dropdown.options)
        .find((option) => option.value === layoutId);
      selectedOption?.remove();
      if (dropdown.options.length === 1) {
        dropdown.options[0].textContent = 'No saved layouts';
      }
      dropdown.selectedIndex = 0;
      try {
        const layouts = await useLayoutStore.getState().loadLayouts(memberId);
        renderLayouts(layouts);
        closeDeleteConfirm();
      } catch (refreshError) {
        confirmError.textContent = `Layout deleted, but the list could not refresh: ${errorText(
          refreshError,
          'layout service unavailable',
        )}`;
        noButton.textContent = 'Close';
      }
    };
    noButton.onclick = () => {
      closeDeleteConfirm();
    };

    confirmModal.append(message, confirmError, yesButton, noButton);
    document.body.appendChild(confirmModal);
  };

  closeButton.onclick = () => {
    closeModal();
  };

  buttonRow.append(loadButton, deleteButton, closeButton);
  modal.append(title, dropdown, error, buttonRow);
  document.body.appendChild(modal);
  if (widgetState) widgetState.managerModal = modal;
  renderLayouts([]);
  loadLayouts();
  return modal;
}
